import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { getProductName } from './useOrderData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useOrderCart = () => {
  const [cart, setCart] = useState([]);
  const [sending, setSending] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const addToCart = useCallback((product) => {
    const productName = typeof product.name === 'string' ? product.name : getProductName(product);
    setCart(prev => {
      if (product.cartId || product.optionsText) {
        return [...prev, { ...product, name: productName, price: Number(product.price) || 0, quantity: 1 }];
      }
      const existing = prev.find(item => item.id === product.id && !item.optionsText && !item.notes);
      if (existing) {
        return prev.map(item => item.id === product.id && !item.optionsText && !item.notes ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...prev, { ...product, name: productName, price: Number(product.price) || 0, quantity: 1 }];
    });
    toast.success(`+ ${productName}`, { duration: 600 });
  }, []);

  const updateQuantity = useCallback((itemKey, delta) => {
    setCart(prev => prev.map(item => {
      const key = item.cartId || item.id;
      if (key === itemKey) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : null;
      }
      return item;
    }).filter(Boolean));
  }, []);

  const updateItemPrice = useCallback((itemKey, newPrice) => {
    const sanitized = Math.max(0, Number(newPrice) || 0);
    setCart(prev => prev.map(item => {
      const key = item.cartId || item.id;
      if (key === itemKey) {
        // Preserve the original/menu price the first time we override
        const originalPrice = item.original_price ?? item.price;
        return { ...item, price: sanitized, original_price: originalPrice, price_overridden: true };
      }
      return item;
    }));
  }, []);

  const addNoteToCartItem = useCallback((itemKey, note) => {
    setCart(prev => prev.map(item => {
      const key = item.cartId || item.id;
      if (key === itemKey) {
        const existingNotes = item.notes || '';
        const newNotes = existingNotes ? `${existingNotes}, ${note}` : note;
        return { ...item, notes: newNotes };
      }
      return item;
    }));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const getCartTotal = useCallback(() => {
    return cart.reduce((sum, item) => sum + ((Number(item.price) || 0) * item.quantity), 0);
  }, [cart]);

  const getCartCount = useCallback(() => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  const handlePayment = useCallback(async (paymentMethod, selectedMesa, onSuccess) => {
    if (cart.length === 0) { toast.error('Añade productos'); return; }
    setSending(true);
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/venta-directa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          mesa_numero: selectedMesa || null,
          items: cart.map(item => ({
            product_id: item.id,
            product_name: item.name,
            quantity: item.quantity,
            price: item.price,
            options: item.selectedOptions || [],
            notes: [item.optionsText, item.notes].filter(Boolean).join(' | '),
            price_overridden: !!item.price_overridden,
            original_price: item.original_price ?? item.price
          })),
          payment_method: paymentMethod,
          notes: ''
        })
      });
      if (response.ok) {
        const data = await response.json();
        setReceiptData({
          sale_id: data.sale_id,
          mesa_numero: data.mesa_numero,
          total: data.total,
          items: data.items || [],
          payment_method: data.payment_method,
          closed_at: data.closed_at
        });
        setCart([]);
        if (onSuccess) onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error');
      }
    } catch (error) {
      toast.error('Error al procesar la venta');
    } finally {
      setSending(false);
    }
  }, [cart]);

  const handleSendToTable = useCallback(async (selectedMesa, onSuccess) => {
    if (cart.length === 0) { toast.error('Añade productos'); return; }
    if (!selectedMesa) { toast.error('Selecciona una mesa'); return; }
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/pedidos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mesa_numero: selectedMesa,
          items: cart.map(item => ({
            product_id: item.id,
            product_name: item.name,
            quantity: item.quantity,
            price: item.price,
            options: item.selectedOptions || [],
            notes: [item.optionsText, item.notes].filter(Boolean).join(' | '),
            price_overridden: !!item.price_overridden,
            original_price: item.original_price ?? item.price
          })),
          notes: '',
          language: 'es'
        })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Enviado a mesa ${selectedMesa} · ${data.order?.destino || ''}`);
        setCart([]);
        if (onSuccess) onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al enviar');
      }
    } catch (error) {
      toast.error('Error al enviar pedido');
    } finally {
      setSending(false);
    }
  }, [cart]);

  return {
    cart, addToCart, updateQuantity, updateItemPrice, addNoteToCartItem, clearCart,
    getCartTotal, getCartCount, handlePayment, handleSendToTable,
    sending, receiptData, setReceiptData
  };
};
